/**
 * The chrome, in English — and the source of truth for the key set.
 *
 * *Rejected: the mockups' dual-DOM*, where both languages sit in the markup and CSS hides one. That
 * is right for a static file and wrong for React: it doubles the DOM, renders text nobody sees, and
 * gives nothing to interpolate a number into. *Rejected: i18next*, a runtime and a plugin chain for a
 * two-locale dictionary with no pluralisation to speak of.
 *
 * **Nothing the server wrote is in here** (SPEC_5 §4.5). Every sentence about the day is `says`, and
 * the console renders it verbatim. These are headings, button labels, the hero's one-word names for
 * the server's exact state names, and the words around the numbers.
 */

export const en = {
  // ── the shell ──────────────────────────────────────────────────────────────
  "app.name": "便當AI",
  "app.skip": "Skip to today",
  "lang.en": "EN",
  "lang.zh": "繁",

  // ── sign in ────────────────────────────────────────────────────────────────
  "a1.title": "便當AI",
  "a1.lede": "Signing in answers exactly one question: are you one of us?",
  "a1.verifying": "Checking…",
  "a1.email": "Email",
  "a1.password": "Password",
  "a1.submit": "Sign in",
  "a1.unanswered": "The server didn't answer. Nothing was signed in; try again.",
  "a1.idle": "Not signed in.",

  // ── the header ─────────────────────────────────────────────────────────────
  "hdr.competition": "Competition",
  "hdr.stream.connecting": "connecting",
  "hdr.stream.live": "live",
  "hdr.stream.reconnecting": "reconnecting",
  "hdr.stream.unreachable": "server unreachable",
  "hdr.signOut": "Sign out",
  "banner.workerDead": "worker dead",

  // ── today ──────────────────────────────────────────────────────────────────
  "today.title": "Today",
  "today.cutoff": "Submit by",
  "today.left": "{span} left",
  "today.passed": "passed",
  "today.download": "Download D-Plan",
  "today.uploading": "Uploading…",
  "today.rerun": "Run again",
  "today.why": "Why run again? (kept with your name)",
  "up.act": "The upload",
  "up.button": "Upload DataPack",
  "up.uploaded": "Uploaded",
  "up.by": "by",

  // ── market data (the evidence pack) ─────────────────────────────────────────
  "pack.title": "Market data",
  "pack.priceNote": "Price note",

  // ── plan problems ──────────────────────────────────────────────────────────
  "checks.title": "Plan problems",
  "checks.fail": "Fail",
  "checks.warn": "Warning",

  // ── the run ────────────────────────────────────────────────────────────────
  "run.title": "What the AI did",
  "run.none": "No run yet.",
  "d2.running": "running",
  "d3.reply": "Reply",
  "d3.refused": "refused",
  "d3.error": "error",
  "d3.rested": "rested",
  "d3.analystWorking": "analyst working",
  "d3.expected": "That pause is expected: the analyst is working in the sandbox and on the web.",
  "d3.elapsed": "elapsed",
  "log.snapshot": "Read the briefing",
  "log.thinking": "Thinking",
  "log.recall": "Recalled notes",
  "log.remember": "Saved a note",
  "log.note_to_next_run": "Left a note for the next run",
  "log.web_search": "Searched the web",
  "log.view": "Opened a file",
  "log.create": "Wrote a file",
  "log.edit": "Edited a file",
  "log.run_command": "Ran a command",
  "log.ask_analyst": "Asked the analyst",
  "log.read_datapacks": "Read past DataPacks",
  "log.read_dplans": "Read past D-Plans",
  "log.use_skill": "Opened a skill",
  "log.submit_plan": "Submitted the plan",

  // ── our portfolio ──────────────────────────────────────────────────────────
  "pf.title": "Our portfolio",
  "pf.none": "No settled DataPack yet.",
  "pf.nav": "NAV",
  "pf.sinceStart": "{pct} since the start",
  "pf.cash": "Cash",
  "pf.holdings": "Holdings",
  "pf.warnings": "Warnings",
  "pf.yesterday": "Yesterday's orders:",
  "pf.excluded": "excluded: {codes}",
  "pf.holdingsTable": "Holdings",
  "pf.code": "Code",
  "pf.name": "Name",
  "pf.weight": "Weight",
  "pf.outside": "outside the 150",
  "pf.overCap": "over the {cap} cap",
  "pf.overBuffer": "over our {cap}",
  "pf.dayOver": "{n} day over its cap",
  "pf.daysOver": "{n} days over its cap",

  // ── skills ─────────────────────────────────────────────────────────────────
  "skills.title": "Skills",
  "skills.none": "No skills yet. The team adds them in git under backend/skills/.",
  "skills.used": "used today",
  "skills.unused": "not used",
  "skills.invalid": "invalid",

  // ── shared ─────────────────────────────────────────────────────────────────
  "ui.loading": "Reading…",
  "ui.retry": "Retry",
  "ui.yourClock": "on your clock",
  "ui.language": "Language",
  "ui.na": "—",
  "info.what": "What is {term}?",
  "info.datapack": "The organizer's file with our account, holdings and yesterday's prices. Download it from E.Sun's portal after ~19:00 each evening (day one's may only appear that morning) and upload it here. The AI starts at 05:30 the next morning, or at once if it's uploaded later.",
  "info.dplan": "Today's trading plan the AI wrote. Download it, upload it on E.Sun's portal and press 送出繳交 before 08:40.",
  "info.cutoff": "The portal closes at {deadline}; we stop at {cutoff} to leave room for mistakes.",
  "info.pack": "Prices, flows and news we fetch ourselves to help the AI decide. Shown only when a source failed.",
  "info.checks": "Rule checks on the plan. A failure stops the plan; a warning is allowed but worth reading.",
  "info.run": "Every step the AI took this morning. Click a step for details.",
  "info.skills": "Playbooks the team wrote that the AI can open, like our multi-factor strategy.",
  "info.portfolio": "Our account as the organizer reports it in the latest DataPack.",
  "info.nav": "Total value of the fund. We start at NT$1bn.",
  "info.cash": "Share not in stocks. Rule 0–25%; we keep 2–20%.",
  "info.holdings": "Number of stocks. Rule 20–30.",
  "info.warnings": "Organizer's rule-break warnings. 3 = disqualified.",
} as const;

export type Keys = keyof typeof en;
